/**
 * Notifications data access — READ side only. Production is entirely
 * server-side (triggers + RPCs, migration 20260718120000); the notifications
 * table has no INSERT policy on purpose, so a client-side producer cannot
 * exist. What clients may do: read their own rows, mark them read, dismiss
 * them, and manage their own mute preferences.
 */
import { supabase } from '@/lib/supabase/client'

export interface NotificationRow {
  id: string
  recipient_user_id: string
  project_id: string | null
  record_id: string | null
  event_type: string
  payload: Record<string, unknown> | null
  actor_user_id: string | null
  priority: 'normal' | 'high'
  is_read: boolean
  read_at: string | null
  is_dismissed: boolean
  created_at: string
  actor?: { full_name: string } | null
}

export interface NotificationEventType {
  event_type: string
  category: 'record' | 'comment' | 'membership' | 'form'
  label_fa: string
  label_en: string
  default_priority: 'normal' | 'high'
  sort_order: number
  is_active: boolean
}

export interface FetchNotificationsOpts {
  page: number
  pageSize?: number
  unreadOnly?: boolean
  eventType?: string
  includeDismissed?: boolean
}

export async function fetchNotifications(
  userId: string,
  projectId: string,
  opts: FetchNotificationsOpts,
): Promise<NotificationRow[]> {
  const pageSize = opts.pageSize ?? 30
  let q = supabase
    .from('notifications')
    .select('*, actor:user_profiles!actor_user_id(full_name)')
    .eq('recipient_user_id', userId)
    .eq('project_id', projectId)
  if (!opts.includeDismissed) q = q.eq('is_dismissed', false)
  if (opts.unreadOnly) q = q.eq('is_read', false)
  if (opts.eventType) q = q.eq('event_type', opts.eventType)
  const { data, error } = await q
    .order('created_at', { ascending: false })
    .range(opts.page * pageSize, opts.page * pageSize + pageSize - 1)
  if (error) throw error
  return (data ?? []) as unknown as NotificationRow[]
}

export async function fetchUnreadCount(userId: string, projectId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('recipient_user_id', userId)
    .eq('project_id', projectId)
    .eq('is_read', false)
    .eq('is_dismissed', false)
  if (error) throw error
  return count ?? 0
}

export async function markRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId)
  if (error) throw error
}

export async function markAllRead(userId: string, projectId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('recipient_user_id', userId)
    .eq('project_id', projectId)
    .eq('is_read', false)
  if (error) throw error
}

/** Dismissed rows are hidden from the default views but never deleted (spec §12.5.3). */
export async function dismissNotification(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_dismissed: true, dismissed_at: new Date().toISOString() })
    .eq('id', notificationId)
  if (error) throw error
}

// ─── Event catalog + mute preferences (the dynamic parts) ────────────────────

export async function fetchEventCatalog(): Promise<NotificationEventType[]> {
  const { data, error } = await supabase
    .from('notification_event_types')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
  if (error) throw error
  return (data ?? []) as NotificationEventType[]
}

/** Event types the user has muted for this project. */
export async function fetchMutedEventTypes(
  userId: string,
  projectId: string,
): Promise<string[]> {
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('event_type')
    .eq('user_id', userId)
    .eq('project_id', projectId)
  if (error) throw error
  return (data ?? []).map((r) => r.event_type)
}

export async function muteEventType(
  userId: string,
  projectId: string,
  eventType: string,
): Promise<void> {
  const { error } = await supabase
    .from('notification_preferences')
    .insert({ user_id: userId, project_id: projectId, event_type: eventType })
  // 23505 = already muted (double-click); that is the desired end state.
  if (error && error.code !== '23505') throw error
}

export async function unmuteEventType(
  userId: string,
  projectId: string,
  eventType: string,
): Promise<void> {
  const { error } = await supabase
    .from('notification_preferences')
    .delete()
    .eq('user_id', userId)
    .eq('project_id', projectId)
    .eq('event_type', eventType)
  if (error) throw error
}
